import { capCustomerModuleStart, CustomerModuleProcess } from "@porsche-customer/formular1-cap-customer-module"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { getCustomerNameRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { useBffDynamic, useCapSamlToken, useConfig } from "src/hooks"
import type { OpenCapFunction } from "src/types/OpenCapFunction"

interface UseCapProps {
	onCustomerSelect: (customer: { businessPartnerId: string; leadId?: string; name: string }) => void
	onError: () => void
	orderTypeCode?: string
}

export const useCap = ({ onCustomerSelect, onError, orderTypeCode }: UseCapProps) => {
	const [openCap, setOpenCap] = useState<OpenCapFunction>()
	const [isCustomerNameLoading, setIsCustomerNameLoading] = useState<boolean>(false)

	const dealerContext = useDealerContext()
	const getCustomerName = useBffDynamic(getCustomerNameRoute, "inline")
	const capSamlToken = useCapSamlToken()
	const { capIntegrationEnabled } = useConfig()

	const selectCustomerFromCap = useCallback(async () => {
		const { businessPartnerId, leadId } = await openCap!()

		if (!businessPartnerId) {
			return
		}

		if (!capSamlToken) {
			onError()
			return
		}

		try {
			setIsCustomerNameLoading(true)

			const { formattedName } = await getCustomerName({
				body: undefined,
				pathParams: { businessPartnerId },
				queryParams: { capSamlToken },
			})

			onCustomerSelect({
				businessPartnerId,
				leadId,
				name: formattedName,
			})
		} catch {
			onError()
		} finally {
			setIsCustomerNameLoading(false)
		}
	}, [capSamlToken, getCustomerName, onCustomerSelect, onError, openCap])

	useEffect(() => {
		if (!capIntegrationEnabled) {
			return
		}

		const capCustomerModuleStartPromise = capCustomerModuleStart({
			country: dealerContext.marketplaceKey,
			dealerContext,
			process: CustomerModuleProcess.PVA_CUSTOMER_ORDER,
		})

		capCustomerModuleStartPromise
			.then((callback) => {
				if (!orderTypeCode) {
					throw new Error("No order type provided")
				}

				setOpenCap(() =>
					callback.bind(null, {
						employee: dealerContext.username,
						importer: dealerContext.marketplaceImporterNumber,
						orderType: orderTypeCode,
						process: CustomerModuleProcess.PVA_CUSTOMER_ORDER,
					}),
				)
			})
			.catch(() => {
				setOpenCap(undefined)
			})
	}, [setOpenCap, capIntegrationEnabled, dealerContext, orderTypeCode])

	return {
		isCapEnabled: capIntegrationEnabled,
		isCapInitialized: capIntegrationEnabled && !!openCap, // avoid setState when disabled
		isCustomerNameLoading,
		selectCustomerFromCap,
	}
}
